import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import closeSvg from 'assets/icons/close.svg';
import downloadSvg from 'assets/icons/download.svg';
import Button, { Color, Size } from 'lib/Button';
import DateRangeSelect from 'lib/DatePicker/DateRangeSelect';
import DropdownMenu from 'lib/DropdownMenu';
import FilterChannel from 'components/common/FilterArea/Filters/FilterChannel/FilterChannel';
import FilterMediaType from 'components/common/FilterArea/Filters/FilterMediaType/FilterMediaType';
import FilterLocation from 'components/common/FilterArea/Filters/FilterLocation';
import {
  AvailabilityReportFilter,
  AvailabilityReportFiltersProps,
} from 'components/pages/AvailabilityReport/AvailabilityReport.types';
import { RowGroupColumn } from 'customHooks/useRowGroupColumns';
import FilterFormats from 'components/common/FilterArea/Filters/FilterFormats';
import FilterNetworks from 'components/common/FilterArea/Filters/FilterNetworks';
import FilterFrameList from 'components/common/FilterArea/Filters/FilterFrameList';
import { Store } from 'components/common/types/Store.types';
import { Environment } from 'components/common/types/Deal.types';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { DropdownGroupBy } from 'components/common/Filters/DropdownGroupBy';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { LocationItem, FormatItem, NetworkItem, FrameList } from './AddFilter/AddFilter.types';
import FilterMetrics from './FilterMetrics';
import AddFilter from './AddFilter';
import useAvailabilityReferenceData from './useAvailabilityReferenceData';

const dateRangeClassName = 'w-full min-w-0 sm:w-[200px] sm:shrink-0 [&>*]:w-full';
const mediaTypeClassName = 'w-full min-w-0 sm:w-[200px] sm:shrink-0 [&>div]:w-full sm:[&>div]:w-48';
const channelClassName = 'w-full min-w-0 sm:w-[200px] sm:shrink-0 [&>*]:w-full sm:[&>*]:w-44';
const rowSpacerClassName = 'grow shrink basis-0 min-w-0 max-sm:hidden';
const advancedFiltersRowClassName = 'flex items-end gap-4 w-full mt-2';
const advancedFiltersChipsClassName = 'flex flex-wrap items-center gap-4 min-w-0 flex-1';
const advancedFiltersActionsClassName = 'flex items-center shrink-0';
const addFilterButtonClassName = 'shrink-0 h-[38px] flex';
const metricsGroupClassName = 'flex gap-4 shrink-0 w-full max-sm:flex-col sm:flex-row sm:flex-nowrap sm:w-auto';
const groupByItemClassName = 'w-full sm:w-auto shrink-0 [&>div]:!w-full sm:[&>div]:!w-40';
const framesItemClassName = 'w-full sm:w-auto shrink-0 [&>div]:!w-full sm:[&>div]:!w-44';

interface ExtendedAvailabilityReportFiltersProps extends AvailabilityReportFiltersProps {
  selectedRowGroupColumns: RowGroupColumn[];
  handleToggleRowGroupColumn: (rowGroupColumns: RowGroupColumn[]) => void;
  rowGroupColumns: RowGroupColumn[];
  selectedMetricType: MetricType;
  handleChangeMetricType: (metricType: MetricType) => void;
  exportGridAsCsv: () => void;
  exportGridAsExcel: () => void;
  exportAllAsCsv: () => void;
  isViewExportDisabled: boolean;
  isExportLoading: boolean;
}

const useFilterArrays = (
  selectedFilters: AvailabilityReportFilter,
): {
  channelsArray: Environment[];
  locationsArray: LocationItem[];
  formatsArray: FormatItem[];
  networksArray: NetworkItem[];
  frameList: FrameList | undefined;
} => {
  const { channels, locations, formats, networks, frameList } = selectedFilters;

  return {
    channelsArray: Array.isArray(channels) ? channels : [],
    locationsArray: Array.isArray(locations) ? (locations as LocationItem[]) : [],
    formatsArray: Array.isArray(formats) ? (formats as FormatItem[]) : [],
    networksArray: Array.isArray(networks) ? (networks as NetworkItem[]) : [],
    frameList,
  };
};

const AvailabilityReportFilters: React.FC<ExtendedAvailabilityReportFiltersProps> = ({
  selectedFilters,
  updateFilters,
  clearFilters,
  selectedRowGroupColumns,
  handleToggleRowGroupColumn,
  rowGroupColumns,
  isDisabled = false,
  selectedMetricType,
  handleChangeMetricType,
  exportGridAsCsv,
  exportGridAsExcel,
  exportAllAsCsv,
  isViewExportDisabled,
  isExportLoading,
}) => {
  const availableChannels = useSelector((state: Store) => state.userOptions.channels);

  const { startDate, endDate, mediaType } = selectedFilters;
  const { channelsArray, locationsArray, formatsArray, networksArray, frameList } = useFilterArrays(selectedFilters);

  const { shouldFetchData } = useAvailabilityReferenceData({
    channelsArray,
    formatsArray,
    locationsArray,
    networksArray,
    mediaType,
    startDate,
    endDate,
    updateFilters,
  });

  return (
    <div className="pt-2">
      <div className="flex flex-wrap items-center gap-4 w-full">
        <div className={dateRangeClassName}>
          <DateRangeSelect
            startDate={startDate ? new Date(startDate) : undefined}
            endDate={endDate ? new Date(endDate) : undefined}
            onStartDateChange={(date) => updateFilters({ startDate: date ? format(date, 'yyyy-MM-dd') : undefined })}
            onEndDateChange={(date) => updateFilters({ endDate: date ? format(date, 'yyyy-MM-dd') : undefined })}
            minDate={new Date(2000, 0, 1)}
            maxDate={new Date(2100, 0, 1)}
            isClearable
            shouldDefaultToDateAfterFromSelection
            dataTestId="availability-date-range-select"
          />
        </div>
        <div className={mediaTypeClassName}>
          <FilterMediaType selectedMediaType={mediaType as DirectSalesMediaType} changeFilters={updateFilters} />
        </div>
        <div className={channelClassName}>
          <FilterChannel
            selectedChannels={channelsArray}
            availableChannels={availableChannels}
            changeFilters={updateFilters}
          />
        </div>
        <div aria-hidden="true" className={rowSpacerClassName} />
        <div className={metricsGroupClassName}>
          <div className={groupByItemClassName}>
            <DropdownGroupBy
              handleToggleRowGroupColumn={handleToggleRowGroupColumn}
              rowGroupColumns={rowGroupColumns}
              selectedRowGroupColumns={selectedRowGroupColumns}
              reportType="Availability"
              isDisabled={isDisabled}
            />
          </div>
          <div className={framesItemClassName}>
            <FilterMetrics
              selectedMetricType={selectedMetricType}
              changeFilters={handleChangeMetricType}
              isDisabled={isDisabled}
            />
          </div>
        </div>
      </div>
      <div className={advancedFiltersRowClassName} data-test-id="availability-advanced-filters-row">
        <div className={advancedFiltersChipsClassName} data-test-id="availability-advanced-filters-chips">
          <div className={addFilterButtonClassName}>
            <AddFilter
              selectedLocations={locationsArray}
              selectedFormats={formatsArray}
              selectedNetworks={networksArray}
              selectedFrameList={frameList}
              changeFilters={updateFilters}
              isDisabled={!shouldFetchData}
            />
          </div>
          {!!locationsArray.length && (
            <div className="shrink-0">
              <FilterLocation
                selectedLocations={locationsArray}
                changeFilters={updateFilters}
                isDisabled={!shouldFetchData}
              />
            </div>
          )}
          {!!formatsArray.length && (
            <div className="shrink-0">
              <FilterFormats
                selectedFormats={formatsArray}
                changeFilters={updateFilters}
                isDisabled={!shouldFetchData}
              />
            </div>
          )}
          {!!networksArray.length && (
            <div className="shrink-0">
              <FilterNetworks
                selectedNetworks={networksArray}
                changeFilters={updateFilters}
                isDisabled={!shouldFetchData}
              />
            </div>
          )}
          {!!(frameList?.routeFrameCodes?.length || frameList?.uploadedFrameLists?.length) && (
            <div className="shrink-0">
              <FilterFrameList
                selectedFrameList={frameList}
                changeFilters={updateFilters}
                isDisabled={!shouldFetchData}
              />
            </div>
          )}
          <div className={addFilterButtonClassName}>
            <Button
              svg={closeSvg}
              label="Clear"
              color={Color.TERTIARY}
              size={Size.FIT_CONTAINER}
              onClick={clearFilters}
            />
          </div>
        </div>
        <div className={advancedFiltersActionsClassName} data-test-id="availability-advanced-filters-actions">
          <DropdownMenu
            dropdownMenuSections={[
              {
                id: 'export-view',
                label: 'Export current view',
                options: [
                  {
                    id: 'availability-export-view-xlsx',
                    label: 'XLSX',
                    onClick: exportGridAsExcel,
                    isDisabled: isViewExportDisabled,
                  },
                  {
                    id: 'availability-export-view-csv',
                    label: 'CSV',
                    onClick: exportGridAsCsv,
                    isDisabled: isViewExportDisabled,
                  },
                ],
              },
              {
                id: 'export-all',
                label: 'Export all',
                options: [
                  {
                    id: 'availability-export-all-csv',
                    label: 'CSV',
                    onClick: exportAllAsCsv,
                    isDisabled: isDisabled || isExportLoading,
                  },
                ],
              },
            ]}
            icon={downloadSvg}
            color={Color.TERTIARY}
            label="Export"
            closeOnSelection
            isDisabled={isViewExportDisabled && (isDisabled || isExportLoading)}
            isSpinner={isExportLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityReportFilters;
